import { useState } from 'react';
import { Modal } from './Modal';

interface ConfirmChallenge {
  /** The exact text the user has to type before the confirm button enables. */
  expected: string;
  /** The field's label, e.g. "Type the brand's name to confirm". */
  label: string;
}

interface ConfirmDialogProps {
  title: string;
  message: string;
  /** Verb-first, e.g. "Delete product". */
  confirmLabel: string;
  /** What the confirm button says while the action runs. */
  busyLabel?: string;
  /** What the way out says. "Keep it" reads right for a delete, not for a change. */
  dismissLabel?: string;
  /**
   * For the deletes that take a whole brand or the account with them: the
   * user retypes something before the button works, so a stray click on a
   * red button cannot do it.
   */
  challenge?: ConfirmChallenge;
  isBusy?: boolean;
  error?: string | null;
  onConfirm: () => void;
  onDismiss: () => void;
}

/**
 * Destructive-action confirmation, for actions that cannot be taken back.
 *
 * Knowledge-base deletes no longer use it: they are optimistic and reversible
 * for six seconds through `useUndoableAction`, which is the better trade when
 * an undo is possible. Reach for this only when it is not.
 */
export function ConfirmDialog({
  title,
  message,
  confirmLabel,
  busyLabel = 'Working',
  dismissLabel = 'Keep it',
  challenge,
  isBusy = false,
  error = null,
  onConfirm,
  onDismiss,
}: ConfirmDialogProps) {
  const [typed, setTyped] = useState('');
  const isArmed = challenge ? typed.trim() === challenge.expected : true;

  return (
    <Modal title={title} kind="confirm" onDismiss={onDismiss}>
      <p className="modal__message">{message}</p>

      {challenge ? (
        <label className="field">
          <span className="field__label">{challenge.label}</span>
          <input
            className="field__input"
            type="text"
            autoComplete="off"
            spellCheck={false}
            placeholder={challenge.expected}
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
          />
        </label>
      ) : null}

      {error ? (
        <p className="form__error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="modal__actions">
        <button className="button" type="button" onClick={onDismiss} disabled={isBusy}>
          {dismissLabel}
        </button>
        <button
          className="button button--danger"
          type="button"
          onClick={onConfirm}
          disabled={isBusy || !isArmed}
        >
          {isBusy ? busyLabel : confirmLabel}
        </button>
      </div>
    </Modal>
  );
}
